import type React from "react";
import {Button, Stack, Tooltip} from "@mui/material";
import UndoIcon from "~/icons/svg/UndoIcon.svg?react";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import {TAXONOMY_CHANGE_META} from "~/pages/taxonomy-builder-v2/constants/taxonomyChangeMeta";

type RevertChangesCellProps = {
    changes: m_taxonomy.Change[];
    canRevert: (change: m_taxonomy.Change) => boolean;
    onRevert: (change: m_taxonomy.Change) => void;
    disabled: boolean;
};

/** Why a change can't be put back, per type (see `canRevert`). */
const BLOCKED: Record<m_taxonomy.ChangeType, string> = {
    removed: 'Another category now has its place, or not all of its sub-categories were deleted with it.',
    renamed: 'Another category under the same parent now has the old name.',
    moved: 'The old parent is gone, or already has a category with this name.',
    added: 'Some of its sub-categories were not added with it.',
    updated: '',
};

/** One button per change on the category: puts that change back in the approver's copy. */
export const RevertChangesCell: React.FC<RevertChangesCellProps> = ({changes, canRevert, onRevert, disabled}) => (
    <Stack alignItems="flex-start" gap={0.5} sx={{py: 1}}>
        {changes.map((change, i) => {
            const possible = canRevert(change);
            return (
                <Tooltip key={i} title={possible ? 'Reject this change' : `Can't be reverted. ${BLOCKED[change.type]}`}>
                    {/* A disabled button gets no hover events: the span keeps the reason showing. */}
                    <span>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<UndoIcon width={18} height={18}/>}
                            disabled={disabled || !possible}
                            onClick={() => onRevert(change)}
                        >
                            {TAXONOMY_CHANGE_META[change.type].label}
                        </Button>
                    </span>
                </Tooltip>
            );
        })}
    </Stack>
);
